<script setup lang="ts">
/**
 * Imatge responsive amb AVIF + fallback JPEG i placeholder blur-up.
 *
 * Les variants i el LQIP els genera `scripts/optimize-images.mjs`. `src` és la
 * ruta pública SENSE extensió ni sufix d'amplada: `/library/sapiens`.
 *
 * El LQIP (WebP de 16px inline) va de background del wrapper: el browser
 * l'escala amb filtre bilineal, cosa que ja produeix l'efecte difuminat sense
 * `filter: blur()` ni una segona capa al DOM. La imatge real hi fa fade a sobre.
 */
import manifest from '~/assets/images.json'

interface Entry {
  w: number
  h: number
  /** Amplades AVIF disponibles. */
  widths: number[]
  /** Amplada única del fallback JPEG. */
  jpeg: number
  lqip: string
}

const props = withDefaults(
  defineProps<{
    /** Ruta pública sense extensió, p.ex. `/img/mission/team1`. */
    src: string
    alt?: string
    /** Amplada renderitzada per breakpoint. Ajustar-ho és el que estalvia bytes. */
    sizes?: string
    /** Només per a l'LCP de la pàgina: desactiva el lazy-load i puja prioritat. */
    priority?: boolean
    /** Classes per a l'<img> (object-fit, aspect-ratio…). */
    imgClass?: string
  }>(),
  { alt: '', sizes: '100vw', priority: false, imgClass: '' },
)

const base = computed(() => props.src.replace(/\.(avif|jpe?g|png|webp)$/i, ''))
const entry = computed<Entry | undefined>(
  () => (manifest as Record<string, Entry>)[base.value],
)
const widths = computed(() => entry.value?.widths ?? [])

const avifSrcset = computed(() =>
  widths.value.map((w) => `${base.value}-${w}.avif ${w}w`).join(', '),
)

/** Sense srcset: qui arriba aquí no té AVIF i tampoc mereix tres variants. */
const fallback = computed(() =>
  entry.value ? `${base.value}-${entry.value.jpeg}.jpg` : props.src,
)

const loaded = ref(false)
const img = useTemplateRef<HTMLImageElement>('img')

/**
 * El fade s'activa només un cop hidratat. Si es pintés `opacity: 0` ja al
 * marcatge del servidor, una hidratació fallida deixaria totes les imatges
 * invisibles per sempre.
 */
const hydrated = ref(false)

onMounted(() => {
  hydrated.value = true
  // Amb la imatge a la cache, `load` pot haver disparat abans d'arribar aquí.
  if (img.value?.complete) loaded.value = true
})
</script>

<template>
  <!-- El wrapper s'ajusta a l'<img>, que ja reserva espai via width/height o via
       la seva classe `aspect-*`. Forçar-hi un aspect-ratio propi descuadraria
       el fons respecte de la imatge quan la crida imposa una altra proporció. -->
  <div
    class="app-image"
    :style="entry && !loaded ? { backgroundImage: `url('${entry.lqip}')` } : undefined"
  >
    <picture>
      <source v-if="widths.length" :srcset="avifSrcset" :sizes="sizes" type="image/avif" />
      <img
        ref="img"
        :src="fallback"
        :alt="alt"
        :width="entry?.w"
        :height="entry?.h"
        :loading="priority ? 'eager' : 'lazy'"
        :fetchpriority="priority ? 'high' : 'auto'"
        decoding="async"
        class="app-image__img"
        :class="[imgClass, { 'is-pending': hydrated && !loaded }]"
        @load="loaded = true"
        @error="loaded = true"
      />
    </picture>
  </div>
</template>

<style scoped>
.app-image {
  background-size: cover;
  background-position: center;
}

/* <picture> no ha de crear caixa: el fons del wrapper ha de quedar exactament
   sota l'<img>, sense el gap de line-height dels elements inline. */
.app-image :deep(picture) {
  display: contents;
}

.app-image__img {
  display: block;
  transition: opacity 400ms ease;
}

.app-image__img.is-pending {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .app-image__img {
    transition: none;
  }
}
</style>
